import {
  Building2,
  Flag,
  Handshake,
  IdCard,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'

import type { CreateEntityDialogSection } from '@/components/library-v2/ui/CreateEntityDialogLayout'

import type { EditableCompanyField } from '@/types/library/Entity'
import {
  COMPANY_FIELDS,
  RELATIONSHIP_STATUS_OPTION_COLORS,
  RELATIONSHIP_STATUS_OPTION_LABELS,
  RELATIONSHIP_STATUS_OPTION_VALUES,
} from '@/types/library/Entity'

type CompanyField = (typeof COMPANY_FIELDS)[number]

const COMPANY_RELATIONSHIP_STATUS_FIELD = {
  key: 'relationship_status',
  label: 'Relationship Status',
  type: 'dropdown',
  required: false,
  placeholder: 'Select relationship status...',
  options: RELATIONSHIP_STATUS_OPTION_VALUES,
  optionLabels: RELATIONSHIP_STATUS_OPTION_LABELS,
  optionColors: RELATIONSHIP_STATUS_OPTION_COLORS,
} satisfies CompanyField

interface CompanyFormSectionConfig {
  id: string
  label: string
  description: string
  icon: CreateEntityDialogSection['icon']
  required?: boolean
  fieldKeys: readonly EditableCompanyField[]
}

const COMPANY_FIELD_BY_KEY = new Map<EditableCompanyField, CompanyField>(
  [...COMPANY_FIELDS, COMPANY_RELATIONSHIP_STATUS_FIELD].map((field) => [
    field.key,
    field,
  ]),
)

export const COMPANY_FORM_SECTION_CONFIGS = [
  {
    id: 'basics',
    label: 'Company Details',
    description:
      'Core account information and how this company fits your network.',
    icon: Building2,
    required: true,
    fieldKeys: [
      'name',
      'relationship_types',
      'primary_contact_entity_person_id',
      'website_url',
      'description',
    ],
  },
  {
    id: 'identity',
    label: 'Legal & Identifiers',
    description: 'Legal names, registration identifiers, and ownership links.',
    icon: IdCard,
    fieldKeys: [
      'legal_name',
      'dba_name',
      'aliases',
      'uei',
      'cage_code',
      'linkedin_url',
      'parent_entity_company_id',
    ],
  },
  {
    id: 'market',
    label: 'Federal Market',
    description:
      'Set-asides, codes, and contract vehicles used for capture work.',
    icon: Handshake,
    fieldKeys: [
      'socioeconomic_categories',
      'naics_codes',
      'psc_codes',
      'vehicles',
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    description: 'SAM status, business size, and facility clearance context.',
    icon: ShieldCheck,
    fieldKeys: [
      'sam_registration_status',
      'sam_expiration_date',
      'facility_clearance_level',
      'company_size',
    ],
  },
  {
    id: 'capabilities',
    label: 'Capabilities',
    description:
      'Industry focus and capability areas relevant to teaming or capture.',
    icon: Sparkles,
    fieldKeys: ['industry', 'core_capabilities', 'capabilities_summary'],
  },
  {
    id: 'account-management',
    label: 'Account Management',
    description: 'Prioritization and recent outreach context for this account.',
    icon: Flag,
    fieldKeys: [
      'relationship_status',
      'priority',
      'last_contacted_at',
      'tag_ids',
    ],
  },
] satisfies readonly CompanyFormSectionConfig[]

export const getCompanyFields = (
  fieldKeys: readonly EditableCompanyField[],
): CompanyField[] => {
  return fieldKeys.flatMap((fieldKey) => {
    const field = COMPANY_FIELD_BY_KEY.get(fieldKey)
    return field ? [field] : []
  })
}
